import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = !token || token.length < 10;

  async function onSubmit(e) {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth.reset_password_too_short")); return; }
    if (password !== confirm) { toast.error(t("auth.reset_password_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.reset_error_generic"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[100svh] pt-32 pb-20 bg-cream" data-testid="reset-password-page">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("auth.reset_section")}</div>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink">{t("auth.reset_title")}</h1>
          <p className="mt-6 text-slate1 leading-relaxed">
            {t("auth.reset_sub")}
          </p>
          <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-slate1 hover:text-ink">
            <ArrowLeft size={14} /> {t("auth.back_to_login")}
          </Link>
        </div>

        {tokenMissing ? (
          <div className="bg-white border border-rose-200 p-8 md:p-10 rounded-sm" data-testid="reset-token-missing">
            <div className="text-[11px] tracking-[0.3em] uppercase text-rose-700">{t("auth.reset_token_missing_header")}</div>
            <h2 className="mt-3 font-display text-3xl text-ink font-light">{t("auth.reset_token_missing_title")}</h2>
            <p className="mt-4 text-slate1">{t("auth.reset_token_missing_body")}</p>
            <Link to="/forgot-password" className="mt-6 inline-block bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift">
              {t("auth.reset_token_missing_cta")}
            </Link>
          </div>
        ) : done ? (
          <div className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="reset-password-done">
            <CheckCircle2 size={32} className="text-gold" />
            <div className="mt-4 text-[11px] tracking-[0.3em] uppercase text-gold">{t("auth.reset_done_header")}</div>
            <h2 className="mt-2 font-display text-3xl text-ink font-light">{t("auth.reset_done_title")}</h2>
            <p className="mt-4 text-slate1">{t("auth.reset_done_body")}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="reset-password-form">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.reset_new")}</label>
            <div className="relative mt-2">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
              <input
                type={show ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.reset_placeholder_new")}
                className="w-full pl-9 pr-10 py-3 border border-ink/20 bg-cream focus:outline-none focus:border-ink rounded-sm"
                data-testid="reset-password-new"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink"
                aria-label={show ? t("auth.reset_hide_password") : t("auth.reset_show_password")}
              >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            <label className="mt-6 block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.reset_confirm")}</label>
            <div className="relative mt-2">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
              <input
                type={show ? "text" : "password"}
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t("auth.reset_placeholder_confirm")}
                className="w-full pl-9 pr-3 py-3 border border-ink/20 bg-cream focus:outline-none focus:border-ink rounded-sm"
                data-testid="reset-password-confirm"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirm}
              className="mt-8 w-full bg-ink text-cream py-4 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-60"
              data-testid="reset-password-submit"
            >
              {loading ? t("auth.reset_submit_loading") : t("auth.reset_submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
